import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, BarChart3, Clock, BookOpen, AlertTriangle, Flame, Award, Trophy, Star, Zap, Brain, Medal, Rocket, Crown, Mail, CheckCircle2, ClipboardCheck, Download, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { subjectLabel } from "./subjectLabels";
import { errMsg } from "./paywall";

const BADGE_ICONS = {
  "first-step": Star, "spark-3": Zap, "flame-7": Flame, "blaze-15": Rocket, "inferno-30": Crown,
  "quiz-rookie": Award, "quiz-champ": Trophy, "curious-mind": Brain, "chapter-explorer": BookOpen, "scholar": Medal,
};

export const TutorProgress = ({ api, authConfig }) => {
  const [data, setData] = useState(null);
  const [streakInfo, setStreakInfo] = useState(null);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [sendErr, setSendErr] = useState("");
  const [mockTests, setMockTests] = useState([]);
  const [compHistory, setCompHistory] = useState([]);
  const [downloadingId, setDownloadingId] = useState("");

  useEffect(() => {
    axios.get(`${api}/tutor/progress`, authConfig).then((r) => setData(r.data)).catch(() => setData({}));
    axios.get(`${api}/tutor/streak`, authConfig).then((r) => setStreakInfo(r.data)).catch(() => {});
    axios.get(`${api}/tutor/mocktests`, authConfig).then((r) => setMockTests(r.data.tests || [])).catch(() => {});
    axios.get(`${api}/tutor/competitive/history`, authConfig).then((r) => setCompHistory(r.data.attempts || [])).catch(() => {});
    // eslint-disable-next-line
  }, []);

  async function downloadReport(testId) {
    setDownloadingId(testId);
    try {
      const r = await axios.get(`${api}/tutor/mocktest/${testId}/report.pdf`, { ...authConfig, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "ShansoTutor-ReportCard.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { /* noop */ }
    setDownloadingId("");
  }

  async function sendReport() {
    setSending(true); setSendErr(""); setSentTo("");
    try {
      const r = await axios.post(`${api}/tutor/report/send-now`, {}, authConfig);
      setSentTo(r.data.sent_to);
      setTimeout(() => setSending(false), 30000);
      return;
    } catch (e) { setSendErr(errMsg(e, "Could not send the report.")); }
    setSending(false);
  }

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-cyan-400" size={24}/></div>;

  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-8 py-8" data-testid="tutor-progress">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">Parent dashboard</h2>
          <p className="text-zinc-500 text-sm">What your child has been learning with the AI teacher.</p>
        </div>
        <div className="text-right">
          <button onClick={sendReport} disabled={sending} data-testid="report-send-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold border border-cyan-400/40 bg-cyan-500/10 text-cyan-200 hover:border-cyan-300 disabled:opacity-50">
            {sending ? <Loader2 size={13} className="animate-spin"/> : <Mail size={13}/>} Email me this week's report
          </button>
          {sentTo && <p className="text-emerald-300 text-[11px] mt-1.5 flex items-center gap-1 justify-end" data-testid="report-sent-msg"><CheckCircle2 size={11}/> Sent to {sentTo}</p>}
          {sendErr && <p className="text-red-300 text-[11px] mt-1.5" data-testid="report-send-error">⚠ {sendErr}</p>}
          <p className="text-zinc-600 text-[10px] mt-1">Also emailed automatically every week</p>
        </div>
      </div>

      {streakInfo && (
        <div className="mb-6 p-4 rounded-2xl border border-orange-400/25 bg-orange-500/5 flex items-center gap-5 flex-wrap" data-testid="streak-card">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border ${streakInfo.current_streak > 0 ? "bg-orange-500/15 border-orange-400/50" : "bg-zinc-900 border-zinc-800"}`}>
              <Flame size={24} className={streakInfo.current_streak > 0 ? "text-orange-400" : "text-zinc-600"}/>
            </div>
            <div>
              <p className="text-2xl font-black text-white" data-testid="streak-current">{streakInfo.current_streak} day{streakInfo.current_streak !== 1 ? "s" : ""}</p>
              <p className="text-[11px] text-zinc-500">Current streak · best {streakInfo.best_streak}</p>
            </div>
          </div>
          <p className="text-xs text-zinc-400 flex-1 min-w-[180px]">
            {streakInfo.studied_today ? "Studied today — streak safe! 🎉" : "No study yet today — one quick doubt keeps the flame alive!"}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <Stat icon={<BookOpen size={15}/>} label="Sessions" value={data.total_sessions || 0} testid="stat-sessions"/>
        <Stat icon={<BarChart3 size={15}/>} label="Teacher replies" value={data.total_teacher_replies || 0} testid="stat-replies"/>
        <Stat icon={<Clock size={15}/>} label="Est. minutes learned" value={data.est_minutes_learned || 0} testid="stat-minutes"/>
        <Stat icon={<Clock size={15}/>} label="Today's usage" value={`${data.used_today || 0}/${data.daily_limit || 40}`} testid="stat-today"/>
      </div>

      {streakInfo?.badges && (
        <div className="mb-8" data-testid="badges-grid">
          <p className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-3">
            Badges · {streakInfo.badges_earned}/{streakInfo.badges.length} earned
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {streakInfo.badges.map((b) => {
              const Icon = BADGE_ICONS[b.id] || Award;
              return (
                <div key={b.id} title={b.desc} data-testid={`badge-${b.id}`}
                  className={`p-3 rounded-xl border text-center transition-colors ${b.earned
                    ? "border-amber-400/40 bg-amber-500/10"
                    : "border-zinc-800 bg-[#0A0A0A] opacity-45"}`}>
                  <Icon size={20} className={`mx-auto mb-1.5 ${b.earned ? "text-amber-300" : "text-zinc-600"}`}/>
                  <p className={`text-[11px] font-bold ${b.earned ? "text-amber-100" : "text-zinc-500"}`}>{b.label}</p>
                  <p className="text-[9px] text-zinc-500 mt-0.5 leading-tight">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {mockTests.length > 0 && (
        <div className="mb-6 p-4 rounded-2xl border border-emerald-400/25 bg-emerald-500/5" data-testid="mock-history">
          <p className="font-bold text-emerald-200 mb-1 flex items-center gap-2"><ClipboardCheck size={14}/> Mock test report cards</p>
          <p className="text-zinc-500 text-xs mb-3">{mockTests.length} paper{mockTests.length > 1 ? "s" : ""} taken · download any report card as PDF</p>
          <ul className="space-y-2">
            {mockTests.map((t) => (
              <li key={t.test_id} className="flex items-center justify-between gap-3 text-sm" data-testid={`mock-history-${t.test_id}`}>
                <div className="min-w-0">
                  <p className="text-zinc-200 truncate">{t.chapter} <span className="text-zinc-500 text-xs">({subjectLabel(t.subject)})</span></p>
                  <p className="text-zinc-600 text-[11px]">{(t.submitted_at || "").slice(0, 10)}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs font-black ${t.pct >= 60 ? "text-emerald-300" : t.pct >= 33 ? "text-amber-300" : "text-red-300"}`}>{t.total}/{t.max_total} · {t.grade}</span>
                  <button onClick={() => downloadReport(t.test_id)} disabled={downloadingId === t.test_id}
                    data-testid={`mock-history-pdf-${t.test_id}`} title="Download report card PDF"
                    className="p-1.5 rounded-lg border border-emerald-400/30 text-emerald-300 hover:border-emerald-300 disabled:opacity-50">
                    {downloadingId === t.test_id ? <Loader2 size={13} className="animate-spin"/> : <Download size={13}/>}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {compHistory.length > 0 && (
        <div className="mb-6 p-4 rounded-2xl border border-violet-400/25 bg-violet-500/5" data-testid="percentile-progress">
          <p className="font-bold text-violet-200 mb-1 flex items-center gap-2"><TrendingUp size={14}/> JEE/NEET percentile progress</p>
          <p className="text-zinc-500 text-xs mb-3">Estimated percentile across {compHistory.length} competitive quiz{compHistory.length > 1 ? "zes" : ""} — chase the rising line!</p>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={compHistory.map((a, i) => ({ name: `#${i + 1}`, date: a.date, chapter: a.chapter, [a.exam]: a.percentile }))} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a"/>
                <XAxis dataKey="name" tick={{ fill: "#71717a", fontSize: 11 }} stroke="#3f3f46"/>
                <YAxis domain={[0, 100]} tick={{ fill: "#71717a", fontSize: 11 }} stroke="#3f3f46"/>
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #3f3f46", borderRadius: 10, fontSize: 12 }}
                  labelStyle={{ color: "#a1a1aa" }} formatter={(v) => [`${v} percentile`, undefined]}/>
                <Legend wrapperStyle={{ fontSize: 11 }}/>
                <Line type="monotone" dataKey="jee" name="JEE" stroke="#A78BFA" strokeWidth={2} dot={{ r: 3 }} connectNulls/>
                <Line type="monotone" dataKey="neet" name="NEET" stroke="#F472B6" strokeWidth={2} dot={{ r: 3 }} connectNulls/>
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-zinc-600 text-[10px] mt-2">Fun estimates for motivation only — not a real rank predictor.</p>
        </div>
      )}

      {data.quiz_stats?.total > 0 && (
        <div className="mb-6 p-4 rounded-2xl border border-purple-400/25 bg-purple-500/5" data-testid="quiz-stats">
          <p className="font-bold text-purple-200 mb-1">Practice quizzes</p>
          <p className="text-zinc-500 text-xs mb-3">{data.quiz_stats.total} taken · average score {data.quiz_stats.avg_pct}%</p>
          <ul className="space-y-1">
            {data.quiz_stats.recent.map((q, i) => (
              <li key={i} className="text-sm text-zinc-200 flex justify-between gap-2">
                <span className="truncate">{q.chapter} <span className="text-zinc-500 text-xs">({subjectLabel(q.subject)})</span></span>
                <span className={`text-xs font-bold shrink-0 ${q.score / q.total >= 0.6 ? "text-emerald-300" : "text-amber-300"}`}>{q.score}/{q.total}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {Object.entries(data.subjects || {}).map(([subj, s]) => (
        <div key={subj} className="mb-6 p-4 rounded-2xl border border-zinc-800 bg-[#0A0A0A]" data-testid={`progress-subject-${subj}`}>
          <p className="font-bold text-white mb-1">{subjectLabel(subj)}</p>
          <p className="text-zinc-500 text-xs mb-2">{s.sessions} sessions · {s.messages} teacher replies</p>
          {s.chapters_touched?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {s.chapters_touched.map((c) => (
                <span key={c} className="px-2 py-0.5 rounded-full text-[11px] bg-cyan-500/10 border border-cyan-400/25 text-cyan-200">{c}</span>
              ))}
            </div>
          )}
        </div>
      ))}

      {data.focus_areas?.length > 0 && (
        <div className="p-4 rounded-2xl border border-amber-400/25 bg-amber-500/5" data-testid="focus-areas">
          <p className="font-bold text-amber-200 mb-2 flex items-center gap-2"><AlertTriangle size={14}/> Needs extra attention</p>
          <p className="text-zinc-400 text-xs mb-3">Chapters where doubts came up most often — worth a revision.</p>
          <ul className="space-y-1">
            {data.focus_areas.map((f) => (
              <li key={f.chapter} className="text-sm text-zinc-200 flex justify-between">
                <span>{f.chapter}</span><span className="text-amber-300/80 text-xs">{f.doubt_sessions} doubt session{f.doubt_sessions > 1 ? "s" : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(data.total_sessions || 0) === 0 && (
        <p className="text-zinc-500 text-sm text-center py-8" data-testid="progress-empty">No learning sessions yet — head to the Learn tab to start!</p>
      )}
    </main>
  );
};

function Stat({ icon, label, value, testid }) {
  return (
    <div className="p-4 rounded-2xl border border-zinc-800 bg-[#0A0A0A]" data-testid={testid}>
      <p className="text-cyan-300 mb-2">{icon}</p>
      <p className="text-2xl font-black text-white">{value}</p>
      <p className="text-[11px] text-zinc-500">{label}</p>
    </div>
  );
}
